// src/features/landing/hooks/useEsimsClient.js
"use client";

import { useCallback, useEffect, useState } from "react";

export function useEsimOffersClient(initial, { limit = 20 } = {}) {
  const [esims, setEsims] = useState(initial?.esims || []);
  const [total, setTotal] = useState(initial?.total || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    offset: initial?.offset ?? 0,
    country: initial?.country ?? "",
    regions: initial?.regions ?? "",
    subType: initial?.subType ?? "",
    priceMin: initial?.priceMin,
    priceMax: initial?.priceMax,
  });

 const load = useCallback(
  async (signal) => {
    const qs = new URLSearchParams();

    // ✅ CRA-compatible param names
  const hasFilter =
  filters.country ||
  filters.regions ||
  filters.subType ||
  filters.priceMin != null ||
  filters.priceMax != null;

// CRA behavior: expand limit when filtering
qs.set("_limit", hasFilter ? "200" : String(limit));
qs.set("_offset", hasFilter ? "0" : String(filters.offset ?? 0));


    if (filters.country) qs.set("country", filters.country);
    if (filters.regions) qs.set("regions", filters.regions);
    if (filters.subType) qs.set("subType", filters.subType);
    if (filters.priceMin != null) qs.set("priceMin", String(filters.priceMin));
    if (filters.priceMax != null) qs.set("priceMax", String(filters.priceMax));

    // ✅ hit the same backend endpoint CRA used
  const res = await fetch(
  `${process.env.NEXT_PUBLIC_API_URL}/catalog/esim/offers?${qs.toString()}`,
  { signal }
);
    // offers are public, but keep this just in case
    if (res.status === 401) {
      setError("Sign in to view eSIM offers.");
      setEsims([]);
      setTotal(0);
      return;
    }

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || "Failed to load eSIMs");

    setEsims(Array.isArray(data?.list) ? data.list : []);
    setTotal(Number.isFinite(data?.total) ? data.total : 0);
    setError(null);
  },
  [filters, limit]
);


  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    load(controller.signal)
      .catch((e) => {
        if (e?.name === "AbortError") return;
        setError(e?.message || "Failed to load eSIMs");
        setEsims([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [load]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v, offset: 0 }));

  return {
    esims,
    total,
    loading,
    error,
    filters,
    setFilters,
    setFilter,
    limit,
    offset: filters.offset ?? 0,
  };
}
